import {
  Button,
  Icon,
  Input,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Text,
  cn,
} from '@flatfile/baseline'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMappingFamily } from '../../api/overrides/mapping'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceHeaderComponent, ResourceRowComponent } from '../../components/ResourceList/types'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import useResource from '../../resources/hooks/useResource'

const customHeaderGenerator = ({ setSort }: { setSort: any }) => {
  const [column, setColumn] = useState('')
  const CustomAppTableHeader: ResourceHeaderComponent = () => {
    return (
      <thead className='border-b border-gray-200'>
        <tr>
          <th className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Namespace
            </Text>
          </th>
          <th className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Name
            </Text>
          </th>
          <th
            className='px-3 py-2 text-left align-middle h-[42px] cursor-pointer'
            onClick={() => {
              setSort({ sortBy: 'errors' })
              setColumn('accuracy')
            }}
          >
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full gap-1'>
              Accuracy
              {column === 'accuracy' && <Icon name='chevron-top' size={12} />}
            </Text>
          </th>
          <th
            className='px-3 py-2 text-left align-middle h-[42px] cursor-pointer'
            onClick={() => {
              setSort({ sortBy: 'errors' })
              setColumn('errors')
            }}
          >
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full gap-1'>
              Changes Made
              {column === 'errors' && <Icon name='chevron-bottom' size={12} />}
            </Text>
          </th>
          <th className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Source
            </Text>
          </th>
          <th
            className='px-3 py-2 text-left align-middle h-[42px] cursor-pointer'
            onClick={() => {
              setSort({ sortBy: 'count' })
              setColumn('count')
            }}
          >
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full gap-1'>
              Total Uploads
              {column === 'count' && <Icon name='chevron-bottom' size={12} />}
            </Text>
          </th>
          <th
            colSpan={2}
            className='px-3 py-2 text-left align-middle h-[42px] cursor-pointer'
            onClick={() => {
              setSort({ sortBy: 'time' })
              setColumn('time')
            }}
          >
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full gap-1'>
              Last Upload
              {column === 'time' && <Icon name='chevron-bottom' size={12} />}
            </Text>
          </th>
          <th className='px-3 py-2 text-left align-middle h-[42px]'></th>
        </tr>
      </thead>
    )
  }
  return CustomAppTableHeader
}

const CustomAppRow: ResourceRowComponent = ({ resourceId }) => {
  const nav = useNavigate()
  const program = useResource<any>('MAPPING_PROGRAMS', resourceId)
  if (!program) {
    return <></>
  }
  return (
    <tr className='hover:bg-gray-50'>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <span className='text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-700'>
            {program.namespace?.length ? program.namespace : 'None'}
          </span>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Input
            defaultValue={program.name}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                program.name = event.currentTarget.value
                updateMappingFamily({
                  environmentId: program.environmentId,
                  familyId: program.id,
                  name: program.name,
                })
              }
            }}
            className='w-[200px]'
            size='small'
          />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' color='primary' className='text-sm'>
            {Math.round((100 * (program.totalRules - program.totalCorrections)) / program.totalRules)}%
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' color='primary' className='text-sm'>
            {program.totalCorrections}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <span className='text-xs font-mono bg-gray-200 px-1.5 py-0.5 rounded text-gray-700'>
            {program.sourceKeys.join(', ')}
          </span>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' color='primary' className='text-sm'>
            {program.totalPrograms}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' color='primary' className='text-sm'>
            {new Date(program.updatedAt).toLocaleDateString()}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' color='primary' className='text-sm'>
            {new Date(program.updatedAt).toLocaleTimeString()}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Button
            size='sm'
            onClick={() => {
              nav(`/apps/mapping/detail/${program.id}`)
            }}
          >
            Drill Down
          </Button>
        </div>
      </td>
    </tr>
  )
}

export const MappingListPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }
  const [requestParams, setRequestParams] = useState<{}>()

  const { addToRefsArray, listHeight } = useDynamicHeight()

  return (
    <div ref={addToRefsArray} className='w-full h-full flex flex-col p-3'>
      <div className='p-3'>
        <Text variant='title2'>Mapping History</Text>
      </div>

      <div>
        <div className='flex justify-end items-center px-2 w-full py-4'>
          <Pagination className='py-2 justify-end' iconOnly size='sm'>
            <PaginationContent>
              <PaginationPrevious
                onClick={() => {
                  if (currentPage > 1) setCurrentPage(currentPage - 1)
                }}
                disabled={currentPage <= 1}
              />
              {(() => {
                // Always show at least one page
                const effectivePageCount = Math.max(1, pagination.pageCount)

                const maxVisiblePages = 5
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                // Adjust start page if we're near the end
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                // Generate array of page numbers to display
                const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                return pages.map((pageNum) => (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      isActive={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                      className={cn(
                        'w-5 h-5 flex items-center justify-center rounded-full p-0',
                        pageNum === currentPage
                          ? 'bg-surface-sentiment-subtle-neutral border-none'
                          : 'hover:bg-gray-100'
                      )}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                ))
              })()}
              <PaginationNext
                onClick={() => {
                  if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                }}
                disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
              />
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      <div className='relative'>
        <div className='overflow-x-auto h-full relative'>
          <ResourceListContextProvider requestParams={requestParams} resourceType='MAPPING_FAMILIES'>
            <ResourceList
              height={listHeight}
              errorMessage='Unable to load programs'
              emptyMessage='No programs to display.'
              headerComponent={customHeaderGenerator({ setSort: setRequestParams })}
              rowComponent={CustomAppRow}
              pagination={pagination}
            />
          </ResourceListContextProvider>
        </div>
      </div>
    </div>
  )
}

export default MappingListPage
